import React from 'react';

import WorkflowGraph from './workflowGraph';
import { activeStep } from '../../domain/cellObjects';
import { workflowBody, workflowAlias } from '../../domain/cellWorkflow';

/**
 * CellWorkflowView — a cell's workflow as it IS: the alias it carries and the steps it runs.
 *
 * This is the READ-ONLY half of the cell panel's Cell Workflow section, extracted so the mission
 * map's dock and the cell detail surface show one thing rather than two renderings of it. Both
 * surfaces answer "what is this cell actually doing", and the answer must not depend on which one
 * you asked.
 *
 * Two rules it exists to hold, both of which the panel already got right:
 *
 *  - **The cell's OWN copy wins.** `cell.props.cell_workflow` is the embedded body, frozen the first
 *    time the cell entered `working`, and it is what the orchestrator walks — so it is what gets
 *    drawn. `workflowBody` consults the palette only to resolve a LEGACY bare alias (cells authored
 *    before the embed decision); it never resolves an alias over a copy, because the palette moves on
 *    and the cell does not.
 *  - **No pickers.** Once a workflow is frozen there is no choice left to describe, and this view
 *    describes none: no mode select, no "Locked" hint, no help text about what could have been
 *    chosen. The graph and the alias are the whole of it.
 *
 * A cell that carries no copy yet — a draft in either inherit mode — has no graph to draw, so the
 * view names the RULE that will pick one instead of showing an empty section. That is the honest
 * answer to "what is this thing doing": nothing yet, and here is what it will run when it starts.
 *
 * Props:
 *   cell      — cell vertex object ({ props })
 *   workflows — the `app/missions/cell_workflows` palette, for the legacy-alias fallback only
 */

// Human-readable labels for CELL_WORKFLOW_MODES (schema.js keeps the wire values snake_case). They
// live beside the read-only view rather than inside the panel because both the panel's mode picker
// and this view name the same three modes, and two spellings of one vocabulary is how they drift.
export const CELL_WORKFLOW_MODE_LABELS = {
  inherit_from_mission: 'Inherit from mission',
  specify_workflow: 'Specify workflow',
  inherit_from_previous_cell: 'Inherit from previous cell',
};

// What to say when the cell carries no workflow of its own. Both inherit modes ARE the answer —
// they name where the copy will come from at dispatch. `specify_workflow` with nothing picked is
// not an answer, so it reads as the nothing it is.
function unresolved(mode) {
  if (!mode || mode === 'specify_workflow') return 'None';
  return CELL_WORKFLOW_MODE_LABELS[mode] || mode;
}

export default function CellWorkflowView({ cell, workflows }) {
  const props = (cell && cell.props) || {};
  // Never the value itself: passing the body to JSX is what took the whole widget down once with
  // "Objects are not valid as a React child" (see domain/cellWorkflow.js).
  const alias = workflowAlias(props.cell_workflow);
  const workflow = workflowBody(props.cell_workflow, workflows);

  if (!alias && !workflow) {
    // Plain body text, not muted: the mode IS the answer to what this cell will run, and muting it
    // presented a real value as a placeholder for one.
    return <p className="mb-0">{unresolved(props.cell_workflow_mode)}</p>;
  }

  return (
    <>
      {/* The same gray-chip properties table the Workspace section below it uses, so the two
          read-only structural facts about a cell are presented the one way. */}
      {alias && (
        <table className="properties">
          <tbody>
            <tr>
              <td className="prop-name-column">Workflow</td>
              <td className="prop-value-column"><code>{alias}</code></td>
            </tr>
          </tbody>
        </table>
      )}
      {/* The step the cell is ON, from the run log — the workflow cursor, and the only thing that
          records it. `props.current_step` was asked for here and is written by nothing, so the
          preview has never marked a step in its life. */}
      {workflow && <WorkflowGraph workflow={workflow} currentStep={activeStep(cell)} />}
    </>
  );
}
